import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { fmtMoney, fmtTs } from "../lib/format";
import type { EaDetailPayload, EaIndicatorRow, IndicatorStat } from "../lib/types";
import { Card, CardTitle, Pill } from "../components/Card";

const SESSIONS = ["asia", "europe", "us"] as const;
type SessionKey = (typeof SESSIONS)[number];

export default function EaDetailPage() {
  const { eaId } = useParams();
  const id = decodeURIComponent(eaId ?? "");
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["ea", id],
    queryFn: () => api.get<EaDetailPayload>(`/admin/api/eas/${encodeURIComponent(id)}`),
    enabled: !!id,
    refetchInterval: 3_000,
    refetchIntervalInBackground: true,
  });

  if (isLoading) return <div className="text-muted">Loading…</div>;
  if (error || !data) {
    return (
      <div className="rounded-md border border-trading-down/40 bg-trading-down/10 px-4 py-3 text-trading-down">
        Failed: {(error as Error)?.message}
      </div>
    );
  }

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ["ea", id] });
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div className="min-w-0 flex-1">
          <Link to="/eas" className="text-xs text-muted hover:text-on-dark">
            ← back to EAs
          </Link>
          <h1 className="mt-1 break-all text-xl font-bold text-on-dark sm:text-display-sm">
            <span className="font-mono">{id}</span>
          </h1>
        </div>
        <span className="text-xs text-muted">
          Updated {fmtTs(data.config.updatedAt)} by {data.config.updatedBy ?? "—"}
        </span>
      </header>

      <PauseCard eaId={id} paused={data.config.paused} onSaved={invalidate} />

      {data.latestHeartbeat ? <LatestHeartbeatCard hb={data.latestHeartbeat} /> : null}

      <IndicatorsCard
        eaId={id}
        knownIndicators={data.knownIndicators}
        indicators={data.indicators}
        onSaved={invalidate}
      />

      <ConfigCard eaId={id} config={data.config} onSaved={invalidate} />

      <IndicatorStatsCard eaId={id} currency={data.latestHeartbeat?.currency ?? null} />

      <EquityCard equity={data.equity} />
    </div>
  );
}

// USC accounts trade in cent units — display PnL in native cents (with USD
// in tooltip) so the number matches the trader's MT5 screen.
function fmtNativePnl(amount: number, currency: string | null): string {
  const cur = (currency ?? "").toUpperCase();
  const sign = amount < 0 ? "-" : "";
  const abs = Math.abs(amount).toFixed(2);
  if (cur === "USC") return `${sign}¢${abs}`;
  return `${sign}$${abs}`;
}

type StatsRange = "today" | "7d" | "30d";
const RANGES: StatsRange[] = ["today", "7d", "30d"];

function IndicatorStatsCard({
  eaId,
  currency,
}: {
  eaId: string;
  currency: string | null;
}) {
  const [range, setRange] = useState<StatsRange>("today");
  const { data, isLoading } = useQuery({
    queryKey: ["ea-indicator-stats", eaId, range],
    queryFn: () =>
      api.get<{ range: string; stats: IndicatorStat[] }>(
        `/admin/api/eas/${encodeURIComponent(eaId)}/indicator-stats?range=${range}`,
      ),
    refetchInterval: 15_000,
  });

  const stats = data?.stats ?? [];
  const totalTrades = stats.reduce((s, r) => s + r.trades, 0);
  const totalPnl = stats.reduce((s, r) => s + r.totalPnl, 0);

  return (
    <Card>
      <CardTitle
        right={
          <div className="flex items-center gap-1 rounded-md bg-surface-elevated p-0.5 text-xs">
            {RANGES.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setRange(r)}
                className={`rounded px-2.5 py-1 transition ${
                  range === r
                    ? "bg-primary text-on-primary font-semibold"
                    : "text-muted hover:text-on-dark"
                }`}
              >
                {r === "today" ? "Today" : r === "7d" ? "7 days" : "30 days"}
              </button>
            ))}
          </div>
        }
      >
        By indicator · realized trades
      </CardTitle>
      {isLoading ? (
        <div className="py-4 text-sm text-muted">Loading…</div>
      ) : stats.length === 0 ? (
        <div className="py-6 text-center text-sm text-muted">
          No closed trades in this window.
        </div>
      ) : (
        <div className="-mx-5 overflow-x-auto px-5">
          <table className="w-full min-w-[820px] text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wider text-muted">
              <tr>
                <th className="py-2">Indicator</th>
                <th className="text-right">Trades</th>
                <th className="text-right">W / L</th>
                <th className="text-right">Win%</th>
                <th className="text-right">Total PnL</th>
                <th className="text-right">Avg PnL</th>
                <th className="text-right">Best</th>
                <th className="text-right">Worst</th>
                <th className="text-right">Avg lot</th>
              </tr>
            </thead>
            <tbody>
              {stats.map((s) => (
                <tr key={s.indicatorType} className="border-t border-hairline-dark">
                  <td className="py-2 font-mono">{s.indicatorType}</td>
                  <td className="text-right font-mono">{s.trades}</td>
                  <td className="text-right font-mono">
                    <span className="text-trading-up">{s.wins}</span>
                    <span className="text-muted"> / </span>
                    <span className="text-trading-down">{s.losses}</span>
                  </td>
                  <td className="text-right font-mono">{s.winRate.toFixed(0)}%</td>
                  <td
                    className={`text-right font-mono ${
                      s.totalPnl < 0
                        ? "text-trading-down"
                        : s.totalPnl > 0
                          ? "text-trading-up"
                          : "text-muted"
                    }`}
                    title={`${fmtMoney(s.totalPnl)} ${currency ?? ""}`}
                  >
                    {fmtNativePnl(s.totalPnl, currency)}
                  </td>
                  <td
                    className={`text-right font-mono ${
                      s.avgPnl < 0 ? "text-trading-down" : s.avgPnl > 0 ? "text-trading-up" : "text-muted"
                    }`}
                  >
                    {fmtNativePnl(s.avgPnl, currency)}
                  </td>
                  <td className="text-right font-mono text-trading-up">
                    {fmtNativePnl(s.bestPnl, currency)}
                  </td>
                  <td className="text-right font-mono text-trading-down">
                    {fmtNativePnl(s.worstPnl, currency)}
                  </td>
                  <td className="text-right font-mono">{s.avgLot.toFixed(2)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-hairline-dark/80 text-on-dark">
                <td className="py-2 font-semibold">Total</td>
                <td className="text-right font-mono">{totalTrades}</td>
                <td colSpan={2}></td>
                <td
                  className={`text-right font-mono ${
                    totalPnl < 0 ? "text-trading-down" : totalPnl > 0 ? "text-trading-up" : "text-muted"
                  }`}
                >
                  {fmtNativePnl(totalPnl, currency)}
                </td>
                <td colSpan={4}></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function PauseCard({
  eaId,
  paused,
  onSaved,
}: {
  eaId: string;
  paused: boolean;
  onSaved: () => void;
}) {
  const mutation = useMutation({
    mutationFn: (newValue: boolean) =>
      api.post(`/admin/api/eas/${encodeURIComponent(eaId)}/pause`, { paused: newValue }),
    onSuccess: onSaved,
  });
  return (
    <Card>
      <CardTitle right="info-events still flow · open positions keep managing">
        Per-EA entry switch
      </CardTitle>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div
            className={`text-lg font-semibold ${paused ? "text-warn" : "text-trading-up"}`}
          >
            {paused ? "Entries paused · existing positions managed" : "Accepting new entries"}
          </div>
          <p className="mt-1 text-sm text-muted">
            Pausing this EA returns empty /api/signals so it can't open new positions.
            TV TP/SL info events keep flowing so already-open positions still close
            normally. Use when one account hit its daily target while others keep running.
          </p>
        </div>
        <button
          type="button"
          className={`self-start ${paused ? "btn-primary" : "btn-secondary"}`}
          onClick={() => mutation.mutate(!paused)}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? "Saving…" : paused ? "Resume entries" : "Pause entries"}
        </button>
      </div>
    </Card>
  );
}

function LatestHeartbeatCard({ hb }: { hb: NonNullable<EaDetailPayload["latestHeartbeat"]> }) {
  const factor = hb.currency === "USC" ? 0.01 : 1;
  return (
    <Card>
      <CardTitle right={`Last beat ${fmtTs(hb.receivedAt)}`}>Account snapshot</CardTitle>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        <Field label="Balance" value={fmtMoney(hb.balance * factor)} sub={hb.currency ?? ""} />
        <Field label="Equity" value={fmtMoney(hb.equity * factor)} />
        <Field
          label="P&L today"
          value={fmtMoney(hb.pnlToday * factor)}
          tone={hb.pnlToday < 0 ? "bad" : hb.pnlToday > 0 ? "good" : "neutral"}
        />
        <Field label="Open positions" value={hb.openPositions} />
        <Field label="Trades today" value={hb.tradesToday ?? "—"} />
        <Field
          label="W / L"
          value={`${hb.winsToday ?? "—"} / ${hb.lossesToday ?? "—"}`}
        />
        <Field
          label="Daily target"
          value={hb.dailyTarget != null ? fmtMoney(hb.dailyTarget * factor) : "—"}
        />
        <Field
          label="Target reached"
          value={hb.targetReached ? "yes" : "no"}
          tone={hb.targetReached ? "good" : "neutral"}
        />
      </div>
    </Card>
  );
}

function Field({
  label,
  value,
  sub,
  tone = "neutral",
}: {
  label: string;
  value: React.ReactNode;
  sub?: string;
  tone?: "good" | "bad" | "warn" | "neutral";
}) {
  const cls =
    tone === "good"
      ? "text-trading-up"
      : tone === "bad"
        ? "text-trading-down"
        : tone === "warn"
          ? "text-warn"
          : "text-on-dark";
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-muted">{label}</div>
      <div className={`mt-0.5 font-mono text-lg font-semibold ${cls}`}>{value}</div>
      {sub ? <div className="text-xs text-muted">{sub}</div> : null}
    </div>
  );
}

function ConfigCard({
  eaId,
  config,
  onSaved,
}: {
  eaId: string;
  config: EaDetailPayload["config"];
  onSaved: () => void;
}) {
  const [dailyTarget, setDailyTarget] = useState(config.dailyTarget);
  const [weeklyTarget, setWeeklyTarget] = useState(config.weeklyTarget ?? 0);
  const [monthlyTarget, setMonthlyTarget] = useState(config.monthlyTarget ?? 0);
  const [dailyMaxLoss, setDailyMaxLoss] = useState(config.dailyMaxLoss ?? 0);
  const [sessions, setSessions] = useState<SessionKey[]>(parseSessions(config.sessions));
  const [applyOpen, setApplyOpen] = useState(false);

  useEffect(() => {
    setDailyTarget(config.dailyTarget);
    setWeeklyTarget(config.weeklyTarget ?? 0);
    setMonthlyTarget(config.monthlyTarget ?? 0);
    setDailyMaxLoss(config.dailyMaxLoss ?? 0);
    setSessions(parseSessions(config.sessions));
  }, [config.dailyTarget, config.weeklyTarget, config.monthlyTarget, config.dailyMaxLoss, config.sessions]);

  const mutation = useMutation({
    mutationFn: () =>
      api.post(`/admin/api/eas/${encodeURIComponent(eaId)}/config`, {
        dailyTarget,
        weeklyTarget,
        monthlyTarget,
        dailyMaxLoss,
        sessions,
      }),
    onSuccess: onSaved,
  });

  return (
    <Card>
      <CardTitle right="target gates (day/week/month) + session window">EA config</CardTitle>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        <div className="flex flex-wrap gap-4">
          <label className="block text-sm">
            <span className="text-muted">Daily target</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={dailyTarget}
              onChange={(e) => setDailyTarget(Number(e.target.value))}
              title="Realized P&L từ 00:00 UTC. Đạt → EA ngừng vào lệnh mới tới hết ngày. 0 = tắt."
              className="mt-1 block w-36 rounded-md border border-hairline-dark bg-canvas-dark px-3 py-1.5 font-mono"
            />
          </label>
          <label className="block text-sm">
            <span className="text-muted">Weekly target</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={weeklyTarget}
              onChange={(e) => setWeeklyTarget(Number(e.target.value))}
              title="Realized P&L từ thứ 2 00:00 UTC. Đạt → ngừng vào lệnh tới hết tuần; sang tuần mới tự reset. 0 = tắt."
              className="mt-1 block w-36 rounded-md border border-hairline-dark bg-canvas-dark px-3 py-1.5 font-mono"
            />
          </label>
          <label className="block text-sm">
            <span className="text-muted">Monthly target</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={monthlyTarget}
              onChange={(e) => setMonthlyTarget(Number(e.target.value))}
              title="Realized P&L từ ngày 1 00:00 UTC. Đạt → ngừng vào lệnh tới hết tháng; sang tháng mới tự reset. 0 = tắt."
              className="mt-1 block w-36 rounded-md border border-hairline-dark bg-canvas-dark px-3 py-1.5 font-mono"
            />
          </label>
          <label className="block text-sm">
            <span className="text-trading-down">Daily max loss</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={dailyMaxLoss}
              onChange={(e) => setDailyMaxLoss(Number(e.target.value))}
              title="Số dương. Realized P&L trong ngày ≤ -giá trị này → EA ngừng vào lệnh tới hết ngày; sang ngày mới tự reset. 0 = tắt."
              className="mt-1 block w-36 rounded-md border border-trading-down/40 bg-canvas-dark px-3 py-1.5 font-mono"
            />
          </label>
        </div>
        <p className="text-xs text-muted">
          Native currency (cent cho USC account). Realized-only — floating P&L không tính.
        </p>
        <fieldset>
          <legend className="text-sm text-muted">Allowed sessions (UTC)</legend>
          <div className="mt-2 flex gap-4">
            {SESSIONS.map((s) => (
              <label key={s} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={sessions.includes(s)}
                  onChange={(e) =>
                    setSessions((prev) =>
                      e.target.checked
                        ? Array.from(new Set([...prev, s]))
                        : prev.filter((x) => x !== s),
                    )
                  }
                />
                <span className="capitalize">{s}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <div className="flex flex-wrap items-center gap-3">
          <button type="submit" className="btn-primary" disabled={mutation.isPending}>
            {mutation.isPending ? "Saving…" : "Save config"}
          </button>
          <button
            type="button"
            className="text-xs text-muted underline-offset-2 hover:text-on-dark hover:underline"
            onClick={() => setApplyOpen(true)}
            title="Copy the SAVED daily target + sessions to other EAs. Save unsaved edits first if you want them included."
          >
            Apply this config to other EAs…
          </button>
          {mutation.isSuccess ? (
            <span className="text-sm text-trading-up">Saved.</span>
          ) : null}
          {mutation.error ? (
            <span className="text-sm text-trading-down">
              {(mutation.error as Error).message}
            </span>
          ) : null}
        </div>
      </form>
      {applyOpen ? (
        <ApplyConfigDialog
          sourceEaId={eaId}
          sourceDailyTarget={config.dailyTarget}
          sourceSessions={config.sessions}
          onClose={() => setApplyOpen(false)}
          onApplied={() => {
            setApplyOpen(false);
            onSaved();
          }}
        />
      ) : null}
    </Card>
  );
}

function ApplyConfigDialog({
  sourceEaId,
  sourceDailyTarget,
  sourceSessions,
  onClose,
  onApplied,
}: {
  sourceEaId: string;
  sourceDailyTarget: number;
  sourceSessions: string;
  onClose: () => void;
  onApplied: () => void;
}) {
  const easQuery = useQuery({
    queryKey: ["admin-eas-list-for-config-apply"],
    queryFn: () => api.get<{ eas: Array<{ eaId: string }> }>(`/admin/api/eas`),
  });
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  const otherEas = (easQuery.data?.eas ?? [])
    .map((e) => e.eaId)
    .filter((id) => id !== sourceEaId)
    .sort();

  const allChecked = otherEas.length > 0 && otherEas.every((id) => selected[id]);
  const toggleAll = (on: boolean) => {
    const next: Record<string, boolean> = {};
    for (const id of otherEas) next[id] = on;
    setSelected(next);
  };

  const mutation = useMutation({
    mutationFn: () => {
      const targetEaIds = otherEas.filter((id) => selected[id]);
      return api.post<{ ok: boolean; written: number }>(
        `/admin/api/eas/${encodeURIComponent(sourceEaId)}/config/apply-to`,
        { targetEaIds },
      );
    },
    onSuccess: () => onApplied(),
    onError: (err) => setError((err as Error).message),
  });

  const selectedCount = otherEas.filter((id) => selected[id]).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="w-full max-w-md rounded-lg border border-hairline-dark bg-canvas-dark p-5">
        <div className="text-base font-semibold text-on-dark">
          Apply EA config from <span className="font-mono">{sourceEaId}</span>
        </div>
        <p className="mt-1 text-sm text-muted">
          Copies the SAVED daily target + sessions onto every ticked EA.
          Each target's <code>paused</code> state and per-indicator allow
          list are preserved (won't unpause a paused EA or wipe out its
          indicator config).
        </p>

        <div className="mt-3 rounded-md border border-hairline-dark bg-surface-elevated/40 p-3 text-xs">
          <div className="mb-1 text-[11px] uppercase tracking-wider text-muted">
            Will copy
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <div>
              <span className="text-muted">Daily target:</span>{" "}
              <span className="font-mono text-on-dark">{sourceDailyTarget}</span>
            </div>
            <div>
              <span className="text-muted">Sessions:</span>{" "}
              <span className="font-mono text-on-dark">{sourceSessions}</span>
            </div>
          </div>
        </div>

        {easQuery.isLoading ? (
          <div className="mt-4 text-sm text-muted">Loading EAs…</div>
        ) : otherEas.length === 0 ? (
          <div className="mt-4 text-sm text-muted">No other EAs to apply to.</div>
        ) : (
          <>
            <div className="mt-4 flex items-center justify-between">
              <label className="flex items-center gap-2 text-xs text-muted">
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={(e) => toggleAll(e.target.checked)}
                />
                <span>Select all ({otherEas.length})</span>
              </label>
              <span className="text-xs text-muted">{selectedCount} selected</span>
            </div>
            <div className="mt-2 max-h-64 space-y-1 overflow-y-auto rounded-md border border-hairline-dark p-2">
              {otherEas.map((id) => (
                <label
                  key={id}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-surface-elevated"
                >
                  <input
                    type="checkbox"
                    checked={!!selected[id]}
                    onChange={(e) =>
                      setSelected((st) => ({ ...st, [id]: e.target.checked }))
                    }
                  />
                  <span className="font-mono text-on-dark">{id}</span>
                </label>
              ))}
            </div>
          </>
        )}

        {error ? (
          <div className="mt-3 text-sm text-trading-down">{error}</div>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-md border border-hairline-dark px-3 py-2 text-sm text-muted hover:text-on-dark"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={mutation.isPending || selectedCount === 0}
            onClick={() => {
              setError(null);
              mutation.mutate();
            }}
          >
            {mutation.isPending
              ? "Applying…"
              : `Apply to ${selectedCount} EA${selectedCount === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function parseSessions(csv: string): SessionKey[] {
  return csv
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter((s): s is SessionKey => SESSIONS.includes(s as SessionKey));
}

interface IndicatorEditState {
  allowed: boolean;
  lot: string;
  closeMode: "broker" | "tv";
  closeOnTp: 1 | 2 | 3 | null;
  closeOnSl: boolean;
  closeOnAlert: boolean;
  tpPips: string;
  slPips: string;
  minSlDistance: string;
  dynTpSl1: string;
  dynTpSl2: string;
  dynTpSl3: string;
  beTriggerPips: string;
  maxPositionsPerSide: string;
  minStackPips: string;
  beOnTp1: boolean;
  slToTp1OnTp2: boolean;
  beEntryOnTp2: boolean;
  useSignalVol: boolean;
  slBufferPips: string;
  maxChasePips: string;
  tp1ClosePct: string;
  tp2ClosePct: string;
  slFromFill: boolean;
  closeAtPnl: string;
  maxLot: string;
  chaseExpiryBars: string;
}

// Indicators whose exit is owned by Pine rather than the legacy TP1/2/3 +
// dynamic-TP-by-SL-distance picker, so the form swaps that picker for an
// info block. LP-live ships its own SL + TP1/2/3 on every signal and closes
// on its own CLOSE event.
// Prefix match, not equality: timeframe-scoped rows arrive as
// "LP-live-M15" / "SMRT-live-M1" / … and every one of them is still an
// LP-live-style indicator for UI purposes. Keep this list in sync with
// LP_LIVE_STYLE_INDICATORS in server-nest/src/common/indicator.constants.ts.
const LP_LIVE_STYLE_PREFIXES = ["LP-LIVE", "SMRT-LIVE", "GRS-LIVE"];
function isLpLiveIndicator(s: string): boolean {
  const u = s.toUpperCase().trim();
  return LP_LIVE_STYLE_PREFIXES.some((p) => u.startsWith(p));
}
function isCloseOnlyIndicator(s: string): boolean {
  return isLpLiveIndicator(s);
}
function hasPineExitAlerts(s: string): boolean {
  return isLpLiveIndicator(s);
}

function buildInitial(
  all: string[],
  indicators: EaIndicatorRow[],
): Record<string, IndicatorEditState> {
  const out: Record<string, IndicatorEditState> = {};
  for (const t of all) {
    const cur = indicators.find((r) => r.indicatorType === t);
    out[t] = {
      // Auto-tick TT-WH (the primary trade strategy alert) when a new
      // indicator row appears; other indicators need an explicit admin nod.
      allowed: cur?.allowed ?? t === "TT-WH",
      lot: cur?.lot != null ? String(cur.lot) : "",
      closeMode: cur?.closeMode ?? "broker",
      closeOnTp: (cur?.closeOnTp as 1 | 2 | 3 | null) ?? null,
      closeOnSl: cur?.closeOnSl ?? true,
      closeOnAlert: cur?.closeOnAlert ?? true,
      tpPips: cur?.tpPips != null ? String(cur.tpPips) : "",
      slPips: cur?.slPips != null ? String(cur.slPips) : "",
      minSlDistance: cur?.minSlDistance != null ? String(cur.minSlDistance) : "",
      dynTpSl1: cur?.dynTpSl1 != null ? String(cur.dynTpSl1) : "",
      dynTpSl2: cur?.dynTpSl2 != null ? String(cur.dynTpSl2) : "",
      dynTpSl3: cur?.dynTpSl3 != null ? String(cur.dynTpSl3) : "",
      beTriggerPips:
        cur?.beTriggerPips != null ? String(cur.beTriggerPips) : "",
      maxPositionsPerSide:
        cur?.maxPositionsPerSide != null ? String(cur.maxPositionsPerSide) : "",
      minStackPips:
        cur?.minStackPips != null ? String(cur.minStackPips) : "",
      beOnTp1: cur?.beOnTp1 === 1,
      slToTp1OnTp2: cur?.slToTp1OnTp2 === 1,
      beEntryOnTp2: cur?.beEntryOnTp2 === 1,
      // Default ON (use volMult) when unset — matches server/EA default.
      useSignalVol: cur?.useSignalVol == null ? true : cur.useSignalVol === 1,
      slBufferPips:
        cur?.slBufferPips != null ? String(cur.slBufferPips) : "",
      maxChasePips:
        cur?.maxChasePips != null ? String(cur.maxChasePips) : "",
      tp1ClosePct: cur?.tp1ClosePct != null ? String(cur.tp1ClosePct) : "",
      tp2ClosePct: cur?.tp2ClosePct != null ? String(cur.tp2ClosePct) : "",
      slFromFill: cur?.slFromFill === 1,
      closeAtPnl: cur?.closeAtPnl != null ? String(cur.closeAtPnl) : "",
      maxLot: cur?.maxLot != null ? String(cur.maxLot) : "",
      chaseExpiryBars:
        cur?.chaseExpiryBars != null ? String(cur.chaseExpiryBars) : "",
    };
  }
  return out;
}

function IndicatorsCard({
  eaId,
  knownIndicators,
  indicators,
  onSaved,
}: {
  eaId: string;
  knownIndicators: string[];
  indicators: EaIndicatorRow[];
  onSaved: () => void;
}) {
  const all = Array.from(
    new Set([...knownIndicators, ...indicators.map((r) => r.indicatorType)]),
  ).sort();
  const [state, setState] = useState<Record<string, IndicatorEditState>>(() =>
    buildInitial(all, indicators),
  );
  const [applyTarget, setApplyTarget] = useState<string | null>(null);
  // Per-indicator collapse — the config block only renders when expanded, so a
  // long list of indicators (LP-live ×3, SMRT ×4, …) stays scannable. Default
  // collapsed; click the chevron on a row to expand + edit.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setState(buildInitial(all, indicators));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(indicators), JSON.stringify(knownIndicators)]);

  const mutation = useMutation({
    mutationFn: () =>
      api.post(`/admin/api/eas/${encodeURIComponent(eaId)}/indicators`, {
        items: all.map((t) => ({
          indicatorType: t,
          allowed: state[t]?.allowed ?? false,
          lot: state[t]?.lot ? Number(state[t].lot) : null,
          closeMode: state[t]?.closeMode ?? "broker",
          // closeOnTp doubles as the LP-live broker TP-level pick (TP1/2/3),
          // so send it for LP-live regardless of closeMode.
          closeOnTp:
            state[t]?.closeMode === "tv" || isLpLiveIndicator(t)
              ? state[t]?.closeOnTp ?? null
              : null,
          closeOnSl: state[t]?.closeMode === "tv" ? state[t]?.closeOnSl ?? true : true,
          closeOnAlert: state[t]?.closeOnAlert ?? true,
          tpPips:
            state[t]?.tpPips && Number(state[t].tpPips) > 0
              ? Math.round(Number(state[t].tpPips))
              : null,
          slPips:
            state[t]?.slPips && Number(state[t].slPips) > 0
              ? Math.round(Number(state[t].slPips))
              : null,
          minSlDistance:
            state[t]?.minSlDistance && Number(state[t].minSlDistance) > 0
              ? Number(state[t].minSlDistance)
              : null,
          dynTpSl1:
            state[t]?.dynTpSl1 && Number(state[t].dynTpSl1) > 0
              ? Number(state[t].dynTpSl1)
              : null,
          dynTpSl2:
            state[t]?.dynTpSl2 && Number(state[t].dynTpSl2) > 0
              ? Number(state[t].dynTpSl2)
              : null,
          dynTpSl3:
            state[t]?.dynTpSl3 && Number(state[t].dynTpSl3) > 0
              ? Number(state[t].dynTpSl3)
              : null,
          beTriggerPips:
            state[t]?.beTriggerPips && Number(state[t].beTriggerPips) > 0
              ? Math.round(Number(state[t].beTriggerPips))
              : null,
          maxPositionsPerSide:
            state[t]?.maxPositionsPerSide && Number(state[t].maxPositionsPerSide) > 0
              ? Math.round(Number(state[t].maxPositionsPerSide))
              : null,
          minStackPips:
            state[t]?.minStackPips && Number(state[t].minStackPips) > 0
              ? Math.round(Number(state[t].minStackPips))
              : null,
          beOnTp1: state[t]?.beOnTp1 ? 1 : 0,
          slToTp1OnTp2: state[t]?.slToTp1OnTp2 ? 1 : 0,
          beEntryOnTp2: state[t]?.beEntryOnTp2 ? 1 : 0,
          useSignalVol: state[t]?.useSignalVol ? 1 : 0,
          slBufferPips:
            state[t]?.slBufferPips && Number(state[t].slBufferPips) > 0
              ? Math.round(Number(state[t].slBufferPips))
              : null,
          maxChasePips:
            state[t]?.maxChasePips && Number(state[t].maxChasePips) > 0
              ? Math.round(Number(state[t].maxChasePips))
              : null,
          tp1ClosePct:
            state[t]?.tp1ClosePct && Number(state[t].tp1ClosePct) > 0
              ? Math.round(Number(state[t].tp1ClosePct))
              : null,
          tp2ClosePct:
            state[t]?.tp2ClosePct && Number(state[t].tp2ClosePct) > 0
              ? Math.round(Number(state[t].tp2ClosePct))
              : null,
          slFromFill: state[t]?.slFromFill ? 1 : 0,
          closeAtPnl:
            state[t]?.closeAtPnl && Number(state[t].closeAtPnl) > 0
              ? Math.round(Number(state[t].closeAtPnl))
              : null,
          maxLot:
            state[t]?.maxLot && Number(state[t].maxLot) > 0
              ? Number(state[t].maxLot)
              : null,
          chaseExpiryBars:
            state[t]?.chaseExpiryBars && Number(state[t].chaseExpiryBars) > 0
              ? Math.round(Number(state[t].chaseExpiryBars))
              : null,
        })),
      }),
    onSuccess: onSaved,
  });

  const allowedCount = all.filter((t) => state[t]?.allowed).length;

  return (
    <Card>
      <CardTitle right={<span>{allowedCount}/{all.length} allowed</span>}>
        Indicator allow-list · per-indicator close mode
      </CardTitle>
      <p className="mb-3 text-xs text-muted">
        Tick allow → set lot override (empty = EA preset) → choose close mode.{" "}
        <code>broker</code> = EA opens with Pine SL/TP (legacy).{" "}
        <code>tv</code> = EA opens free-running and closes on the picked TV TP/SL alert.
      </p>
      <div className="space-y-3">
        {all.map((t) => {
          const s = state[t];
          if (!s) return null;
          const checked = s.allowed;
          return (
            <div
              key={t}
              className={`rounded-md border p-3 ${
                checked
                  ? "border-primary/40 bg-primary/[0.04]"
                  : "border-hairline-dark bg-canvas-dark opacity-70"
              }`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setExpanded((ex) => ({ ...ex, [t]: !ex[t] }))}
                    className="w-4 text-xs text-muted hover:text-on-dark"
                    title={expanded[t] ? "Thu gọn" : "Mở rộng cấu hình"}
                  >
                    {expanded[t] ? "▾" : "▸"}
                  </button>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) =>
                        setState((st) => ({
                          ...st,
                          [t]: { ...st[t], allowed: e.target.checked },
                        }))
                      }
                    />
                    <span className="font-mono text-on-dark">{t}</span>
                    {t === "TT-WH" || t === "MAIN" ? (
                      <Pill tone="primary">primary</Pill>
                    ) : null}
                    {checked && s.closeMode === "tv" ? <Pill tone="warn">TV-close</Pill> : null}
                    {checked && !expanded[t] ? (
                      <span className="text-[10px] text-muted">
                        · TP{s.closeOnTp ?? 1}
                        {isLpLiveIndicator(t) && s.maxChasePips ? ` · chase ${s.maxChasePips}` : ""}
                        {s.beOnTp1 ? " · BE-TP1" : ""}
                      </span>
                    ) : null}
                  </label>
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-xs">
                    <span className="text-muted">lot</span>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="preset"
                      className="w-24 rounded-md border border-hairline-dark bg-canvas-dark px-2 py-1 font-mono"
                      value={s.lot}
                      disabled={!checked}
                      onChange={(e) =>
                        setState((st) => ({ ...st, [t]: { ...st[t], lot: e.target.value } }))
                      }
                    />
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <span className="text-muted">TP pip</span>
                    <input
                      type="number"
                      step="1"
                      min="1"
                      max="10000"
                      placeholder="off"
                      title="Broker TP distance in pips. Empty = no broker TP (position closes only when the matching TV alert arrives). For TT-WH the Pine signal TP still wins when present."
                      className="w-20 rounded-md border border-hairline-dark bg-canvas-dark px-2 py-1 font-mono"
                      value={s.tpPips}
                      disabled={!checked}
                      onChange={(e) =>
                        setState((st) => ({
                          ...st,
                          [t]: { ...st[t], tpPips: e.target.value },
                        }))
                      }
                    />
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <span className="text-muted">SL pip</span>
                    <input
                      type="number"
                      step="1"
                      min="1"
                      max="10000"
                      placeholder="off"
                      title="Broker SL distance in pips. Empty = no broker SL (position closes only when the matching TV alert arrives). For TT-WH the Pine signal SL still wins when present."
                      className="w-20 rounded-md border border-hairline-dark bg-canvas-dark px-2 py-1 font-mono"
                      value={s.slPips}
                      disabled={!checked}
                      onChange={(e) =>
                        setState((st) => ({
                          ...st,
                          [t]: { ...st[t], slPips: e.target.value },
                        }))
                      }
                    />
                  </label>
                </div>
              </div>

              {checked && expanded[t] ? (
                <div className="mt-3 space-y-3 border-t border-hairline-dark pt-3">
                  <label className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="text-muted">Min SL distance</span>
                    <input
                      type="number"
                      step="0.5"
                      min="0.1"
                      max="10000"
                      placeholder="off"
                      title="Skip signals where Pine's |sl - entry| is below this value (price units; for XAU = USD). Empty = no filter. Applies to TT-WH / TT-WH-m5 only — close-only indicators like LORENTZIAN ignore this."
                      className="w-20 rounded-md border border-hairline-dark bg-canvas-dark px-2 py-1 font-mono text-xs"
                      value={s.minSlDistance}
                      onChange={(e) =>
                        setState((st) => ({
                          ...st,
                          [t]: { ...st[t], minSlDistance: e.target.value },
                        }))
                      }
                    />
                    <span className="text-xs text-muted">
                      ($ for XAU — drop signals with SL closer than this)
                    </span>
                  </label>

                  <label className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="text-muted">Break-even trigger (pip)</span>
                    <input
                      type="number"
                      step="1"
                      min="1"
                      max="10000"
                      placeholder="off"
                      title="When position moves this many pips in its favour, EA modifies broker SL to the entry price (locks in zero-loss). Empty = feature off."
                      className="w-20 rounded-md border border-hairline-dark bg-canvas-dark px-2 py-1 font-mono text-xs"
                      value={s.beTriggerPips}
                      onChange={(e) =>
                        setState((st) => ({
                          ...st,
                          [t]: { ...st[t], beTriggerPips: e.target.value },
                        }))
                      }
                    />
                    <span className="text-xs text-muted">
                      (pip; price moves this far → drag SL = entry)
                    </span>
                  </label>

                  {isLpLiveIndicator(t) ? (
                  <div className="rounded-md border border-hairline-dark/50 bg-canvas-dark/50 p-2 space-y-3">
                    <div className="text-[11px] uppercase tracking-wider text-muted">
                      {t} · TP level · stack gates · ladder
                    </div>
                    <label className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="text-muted">Max positions per side</span>
                      <input
                        type="number"
                        step="1"
                        min="1"
                        max="1000"
                        placeholder="off"
                        title="Cap the simultaneously-open positions on EACH side. Set 5 → max 5 BUY and max 5 SELL at once. EA rejects fresh signals beyond the cap with reason `max_positions_per_side_reached`. Empty = unlimited."
                        className="w-20 rounded-md border border-hairline-dark bg-canvas-dark px-2 py-1 font-mono text-xs"
                        value={s.maxPositionsPerSide}
                        onChange={(e) =>
                          setState((st) => ({
                            ...st,
                            [t]: { ...st[t], maxPositionsPerSide: e.target.value },
                          }))
                        }
                      />
                      <span className="text-xs text-muted">
                        (vd 5 → tối đa 5 BUY + 5 SELL; trống = không giới hạn)
                      </span>
                    </label>
                    <label className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="text-muted">Min stack distance (pip)</span>
                      <input
                        type="number"
                        step="1"
                        min="1"
                        max="10000"
                        placeholder="off"
                        title="Same-side stacks must be at least this many pips apart. New BUY rejected if any live BUY of this indicator is within this distance of current ask (same gate for SELL vs bid). Prevents machine-gun stacking in a tight range."
                        className="w-20 rounded-md border border-hairline-dark bg-canvas-dark px-2 py-1 font-mono text-xs"
                        value={s.minStackPips}
                        onChange={(e) =>
                          setState((st) => ({
                            ...st,
                            [t]: { ...st[t], minStackPips: e.target.value },
                          }))
                        }
                      />
                      <span className="text-xs text-muted">
                        (vd 100 → lệnh mới phải cách lệnh cùng chiều ≥ 100 pip)
                      </span>
                    </label>
                    <label className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="text-muted">SL buffer (pip)</span>
                      <input
                        type="number"
                        step="1"
                        min="1"
                        max="10000"
                        placeholder="off"
                        title="Nới SL ra xa entry thêm số pip này khi mở lệnh — bù spread/lệch giá giữa sàn signal (OANDA) và broker (Exness). TP giữ nguyên. Trống = không nới."
                        className="w-20 rounded-md border border-hairline-dark bg-canvas-dark px-2 py-1 font-mono text-xs"
                        value={s.slBufferPips}
                        onChange={(e) =>
                          setState((st) => ({
                            ...st,
                            [t]: { ...st[t], slBufferPips: e.target.value },
                          }))
                        }
                      />
                      <span className="text-xs text-muted">
                        (nới SL xa entry thêm N pip; bù trượt giá giữa sàn)
                      </span>
                    </label>
                    <label className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="text-muted">Chờ limit nếu lệch (pip)</span>
                      <input
                        type="number"
                        step="1"
                        min="1"
                        max="10000"
                        placeholder="off"
                        title="Nếu giá hiện tại lệch khỏi entry của signal QUÁ số pip này theo hướng xấu (đu giá), EA đặt LIMIT tại entry chờ giá về thay vì vào market. Chờ tối đa 5 nến rồi bỏ. Trống = luôn vào market."
                        className="w-20 rounded-md border border-hairline-dark bg-canvas-dark px-2 py-1 font-mono text-xs"
                        value={s.maxChasePips}
                        onChange={(e) =>
                          setState((st) => ({
                            ...st,
                            [t]: { ...st[t], maxChasePips: e.target.value },
                          }))
                        }
                      />
                      <span className="text-xs text-muted">
                        (lệch &gt; N pip → đặt limit tại entry, chờ M nến, hết hạn bỏ)
                      </span>
                    </label>
                    <label className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="text-muted">Chờ limit (số nến)</span>
                      <input
                        type="number"
                        step="1"
                        min="1"
                        max="300"
                        placeholder="5"
                        title="Số cây nến chờ limit khớp; quá số này chưa khớp thì EA huỷ limit. Trống = mặc định 5 nến."
                        className="w-20 rounded-md border border-hairline-dark bg-canvas-dark px-2 py-1 font-mono text-xs"
                        value={s.chaseExpiryBars}
                        onChange={(e) =>
                          setState((st) => ({
                            ...st,
                            [t]: { ...st[t], chaseExpiryBars: e.target.value },
                          }))
                        }
                      />
                      <span className="text-xs text-muted">
                        (quá N nến chưa khớp → huỷ limit · trống = 5)
                      </span>
                    </label>
                    <label className="flex flex-wrap items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={s.useSignalVol}
                        onChange={(e) =>
                          setState((st) => ({
                            ...st,
                            [t]: { ...st[t], useSignalVol: e.target.checked },
                          }))
                        }
                      />
                      <span className="text-muted">Dùng vol từ signal (volMult)</span>
                      <span className="text-xs text-muted">
                        {s.useSignalVol
                          ? "(lot = lot gốc × volMult của signal)"
                          : "(bỏ volMult — mở đúng lot gốc đã set)"}
                      </span>
                    </label>
                    <label className="flex flex-wrap items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={s.slFromFill}
                        onChange={(e) =>
                          setState((st) => ({
                            ...st,
                            [t]: { ...st[t], slFromFill: e.target.checked },
                          }))
                        }
                      />
                      <span className="text-muted">SL theo giá khớp thực</span>
                      <span className="text-xs text-muted">
                        {s.slFromFill
                          ? "(giữ khoảng cách SL của signal tính từ giá khớp; entry tốt hơn → SL theo mình, entry xấu hơn → giữ SL signal)"
                          : "(SL lấy đúng giá SL của signal — mặc định)"}
                      </span>
                    </label>
                    <label className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="text-muted">Chốt khi lãi đạt (PnL)</span>
                      <input
                        type="number"
                        step="1"
                        min="1"
                        placeholder="off"
                        title="Mỗi lệnh đang mở của indicator này, hễ LÃI NỔI đạt số này (đơn vị tiền tài khoản) → đóng lệnh đó ngay, bất kể TP. Trống = tắt."
                        className="w-24 rounded-md border border-hairline-dark bg-canvas-dark px-2 py-1 font-mono text-xs"
                        value={s.closeAtPnl}
                        onChange={(e) =>
                          setState((st) => ({
                            ...st,
                            [t]: { ...st[t], closeAtPnl: e.target.value },
                          }))
                        }
                      />
                      <span className="text-xs text-muted">
                        (lãi nổi mỗi lệnh ≥ N → đóng ngay, bất kể TP · trống = tắt)
                      </span>
                    </label>
                    <label className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="text-muted">Max lot</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        placeholder="off"
                        title="Trần lot. Sau khi nhân volMult, nếu lot > số này → cắt về đúng số này (làm tròn xuống theo lot step). Trống = không giới hạn."
                        className="w-24 rounded-md border border-hairline-dark bg-canvas-dark px-2 py-1 font-mono text-xs"
                        value={s.maxLot}
                        onChange={(e) =>
                          setState((st) => ({
                            ...st,
                            [t]: { ...st[t], maxLot: e.target.value },
                          }))
                        }
                      />
                      <span className="text-xs text-muted">
                        (lot × volMult &gt; N → cap về N · trống = không giới hạn)
                      </span>
                    </label>
                    <label className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="text-muted">Broker TP</span>
                      <select
                        title="EA đặt broker TP ở mức nào của signal khung này. TP1 gần nhất → TP3 xa nhất. SL luôn lấy từ signal."
                        className="rounded-md border border-hairline-dark bg-canvas-dark px-2 py-1 font-mono text-xs"
                        value={String(s.closeOnTp ?? 1)}
                        onChange={(e) =>
                          setState((st) => ({
                            ...st,
                            [t]: { ...st[t], closeOnTp: Number(e.target.value) as 1 | 2 | 3 },
                          }))
                        }
                      >
                        <option value="1">TP1</option>
                        <option value="2">TP2</option>
                        <option value="3">TP3</option>
                      </select>
                      <span className="text-xs text-muted">(TP1 gần nhất → TP3 xa nhất)</span>
                    </label>
                    <label className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="text-muted">Đóng % @ TP1 / TP2</span>
                      <input
                        type="number"
                        step="1"
                        min="1"
                        max="99"
                        placeholder="off"
                        title="Chạm TP1 → đóng N% lot GỐC (partial). Phần còn lại chạy tiếp. Trống = không đóng phần. Cần Broker TP = TP3 để phần còn lại chạy."
                        className="w-16 rounded-md border border-hairline-dark bg-canvas-dark px-2 py-1 font-mono text-xs"
                        value={s.tp1ClosePct}
                        onChange={(e) =>
                          setState((st) => ({ ...st, [t]: { ...st[t], tp1ClosePct: e.target.value } }))
                        }
                      />
                      <span className="text-muted">/</span>
                      <input
                        type="number"
                        step="1"
                        min="1"
                        max="99"
                        placeholder="off"
                        title="Chạm TP2 → đóng thêm N% lot GỐC. TP1%+TP2% phải < 100 (chừa runner)."
                        className="w-16 rounded-md border border-hairline-dark bg-canvas-dark px-2 py-1 font-mono text-xs"
                        value={s.tp2ClosePct}
                        onChange={(e) =>
                          setState((st) => ({ ...st, [t]: { ...st[t], tp2ClosePct: e.target.value } }))
                        }
                      />
                      <span className="text-xs text-muted">
                        (% lot gốc đóng ở TP1 / TP2; còn lại chạy — nên đặt Broker TP=TP3)
                      </span>
                    </label>
                    {(() => {
                      const closeLvl = s.closeOnTp ?? 1;
                      // TP1→SL=entry only meaningful when the leg runs past
                      // TP1 (Broker TP ≥ TP2); TP2→SL=TP1 only past TP2.
                      const beActive = closeLvl >= 2;
                      const ladderActive = closeLvl >= 3;
                      return (
                        <>
                          <label
                            className={`flex flex-wrap items-center gap-2 text-sm ${beActive ? "" : "opacity-50"}`}
                          >
                            <input
                              type="checkbox"
                              checked={s.beOnTp1}
                              onChange={(e) =>
                                setState((st) => ({
                                  ...st,
                                  [t]: { ...st[t], beOnTp1: e.target.checked },
                                }))
                              }
                            />
                            <span className="text-muted">SL → entry khi chạm TP1</span>
                            <span className="text-xs text-muted">
                              {beActive
                                ? "(đạt TP1 → SL về entry; nếu có cả field pip trên → lấy mốc sớm hơn)"
                                : "(cần Broker TP ≥ TP2 mới có tác dụng)"}
                            </span>
                          </label>
                          <label
                            className={`flex flex-wrap items-center gap-2 text-sm ${ladderActive ? "" : "opacity-50"}`}
                          >
                            <input
                              type="checkbox"
                              checked={s.slToTp1OnTp2}
                              onChange={(e) =>
                                setState((st) => ({
                                  ...st,
                                  [t]: { ...st[t], slToTp1OnTp2: e.target.checked },
                                }))
                              }
                            />
                            <span className="text-muted">SL → TP1 khi chạm TP2</span>
                            <span className="text-xs text-muted">
                              {ladderActive
                                ? "(đạt TP2 → kéo SL lên TP1, khoá lãi TP1)"
                                : "(cần Broker TP = TP3 mới có tác dụng)"}
                            </span>
                          </label>
                          <label
                            className={`flex flex-wrap items-center gap-2 text-sm ${ladderActive ? "" : "opacity-50"}`}
                          >
                            <input
                              type="checkbox"
                              checked={s.beEntryOnTp2}
                              onChange={(e) =>
                                setState((st) => ({
                                  ...st,
                                  [t]: { ...st[t], beEntryOnTp2: e.target.checked },
                                }))
                              }
                            />
                            <span className="text-muted">SL → entry khi chạm TP2</span>
                            <span className="text-xs text-muted">
                              {ladderActive
                                ? "(đạt TP2 → SL về entry / hoà vốn; nếu bật cả “SL → TP1” thì TP1 thắng)"
                                : "(cần Broker TP = TP3 mới có tác dụng)"}
                            </span>
                          </label>
                        </>
                      );
                    })()}
                  </div>
                  ) : null}

                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={s.closeOnAlert}
                      onChange={(e) =>
                        setState((st) => ({
                          ...st,
                          [t]: { ...st[t], closeOnAlert: e.target.checked },
                        }))
                      }
                    />
                    <span className="text-on-dark">Close on TV alert</span>
                    <span className="text-xs text-muted">
                      (off = ignore every TP_HIT / SL_HIT / CLOSE_* alert — only broker
                      TP/SL or next flip closes)
                    </span>
                  </label>

                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[11px] uppercase tracking-wider text-muted">
                      Close mode
                    </span>
                    <div className="flex items-center gap-1 rounded-md bg-surface-elevated p-0.5 text-xs">
                      {(["broker", "tv"] as const).map((m) => (
                        <button
                          key={m}
                          type="button"
                          onClick={() =>
                            setState((st) => ({ ...st, [t]: { ...st[t], closeMode: m } }))
                          }
                          className={`rounded px-2.5 py-1 transition ${
                            s.closeMode === m
                              ? "bg-primary text-on-primary font-semibold"
                              : "text-muted hover:text-on-dark"
                          }`}
                        >
                          {m === "broker" ? "Broker SL/TP" : "TV-driven close"}
                        </button>
                      ))}
                    </div>
                  </div>

                  {s.closeMode === "tv" ? (
                    isCloseOnlyIndicator(t) ? (
                      // No-Pine-geometry indicators: hide the TP1/2/3 picks +
                      // dynamic-TP UI entirely. Two flavours of explainer
                      // depending on whether Pine actively closes positions
                      // (ISB / OCC) or just emits BUY/SELL entries (MACD,
                      // SCALPING) and leaves close to flip / broker / manual.
                      <div className="rounded-md border border-trading-up/30 bg-trading-up/5 px-3 py-2">
                        <div className="text-[11px] uppercase tracking-wider text-trading-up">
                          Auto-close source
                        </div>
                        <div className="mt-1 text-sm text-on-dark">
                          {hasPineExitAlerts(t) ? (
                            <>
                              Pine owns the exit for this indicator (fires{" "}
                              <code className="text-trading-up">CLOSE_*</code> /{" "}
                              <code className="text-trading-up">TP_HIT</code> /{" "}
                              <code className="text-trading-up">SL_HIT</code>). EA closes
                              the matching position the moment that alert arrives — no
                              TP / SL picks needed.
                            </>
                          ) : (
                            <>
                              Strategy indicator — only emits BUY / SELL. EA closes the
                              position via flip signal (opposite BUY/SELL), broker SL/TP
                              (set the SL pip / TP pip above), or admin manual close. No
                              TP1/2/3 picks apply here.
                            </>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-md border border-warn/30 bg-warn/5 px-3 py-2 space-y-2">
                        <div className="text-[11px] uppercase tracking-wider text-warn">
                          TV-driven close picks
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                          <span className="text-muted">Default TP (when dynamic empty):</span>
                          {[1, 2, 3].map((n) => (
                            <label key={n} className="flex items-center gap-1.5">
                              <input
                                type="radio"
                                name={`tp-${t}`}
                                checked={s.closeOnTp === (n as 1 | 2 | 3)}
                                onChange={() =>
                                  setState((st) => ({
                                    ...st,
                                    [t]: { ...st[t], closeOnTp: n as 1 | 2 | 3 },
                                  }))
                                }
                              />
                              <span className="font-mono">TP{n}</span>
                            </label>
                          ))}
                          <label className="flex items-center gap-1.5">
                            <input
                              type="radio"
                              name={`tp-${t}`}
                              checked={s.closeOnTp === null}
                              onChange={() =>
                                setState((st) => ({
                                  ...st,
                                  [t]: { ...st[t], closeOnTp: null },
                                }))
                              }
                            />
                            <span className="text-muted">none</span>
                          </label>
                        </div>
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={s.closeOnSl}
                            onChange={(e) =>
                              setState((st) => ({
                                ...st,
                                [t]: { ...st[t], closeOnSl: e.target.checked },
                              }))
                            }
                          />
                          <span>Close on TV SL_HIT</span>
                        </label>

                        <div className="mt-2 border-t border-hairline-dark/60 pt-2">
                          <div className="mb-1 text-[11px] uppercase tracking-wider text-muted">
                            Dynamic TP by SL distance ($ for XAU)
                          </div>
                          <p className="mb-2 text-xs text-muted">
                            When set, the EA picks TP level by the signal's own SL
                            distance instead of the fixed pick above. Larger SL →
                            further TP. Leave all empty to use the fixed TP pick.
                          </p>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                            {([
                              ["dynTpSl1", "TP1 if SL ≥", "dynTpSl1"],
                              ["dynTpSl2", "TP2 if SL ≥", "dynTpSl2"],
                              ["dynTpSl3", "TP3 if SL ≥", "dynTpSl3"],
                            ] as const).map(([key, label]) => (
                              <label key={key} className="flex items-center gap-1.5">
                                <span className="text-muted">{label}</span>
                                <input
                                  type="number"
                                  step="0.5"
                                  min="0.1"
                                  placeholder="—"
                                  className="w-16 rounded-md border border-hairline-dark bg-canvas-dark px-2 py-1 font-mono text-xs"
                                  value={s[key]}
                                  onChange={(e) =>
                                    setState((st) => ({
                                      ...st,
                                      [t]: { ...st[t], [key]: e.target.value },
                                    }))
                                  }
                                />
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    )
                  ) : (
                    <div className="text-xs text-muted">
                      Broker mode — Pine's SL/TP1/TP2/TP3 are sent to the EA; broker
                      fires SL/TP directly. The EA's `.set` file still owns trailing /
                      partial-close.
                    </div>
                  )}

                  <div className="flex items-center justify-end border-t border-hairline-dark/40 pt-2">
                    <button
                      type="button"
                      className="text-xs text-muted underline-offset-2 hover:text-on-dark hover:underline"
                      onClick={() => setApplyTarget(t)}
                      title="Copy the SAVED config for this indicator to other EAs. Save unsaved changes first if you want them included."
                    >
                      Apply this {t} config to other EAs…
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="btn-primary"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? "Saving…" : "Save indicators"}
        </button>
        {mutation.isSuccess ? (
          <span className="text-sm text-trading-up">Saved.</span>
        ) : null}
        {mutation.error ? (
          <span className="text-sm text-trading-down">
            {(mutation.error as Error).message}
          </span>
        ) : null}
      </div>
      {applyTarget ? (
        <ApplyIndicatorDialog
          sourceEaId={eaId}
          indicatorType={applyTarget}
          onClose={() => setApplyTarget(null)}
          onApplied={() => {
            setApplyTarget(null);
            onSaved();
          }}
        />
      ) : null}
    </Card>
  );
}

function ApplyIndicatorDialog({
  sourceEaId,
  indicatorType,
  onClose,
  onApplied,
}: {
  sourceEaId: string;
  indicatorType: string;
  onClose: () => void;
  onApplied: () => void;
}) {
  const easQuery = useQuery({
    queryKey: ["admin-eas-list-for-apply"],
    queryFn: () => api.get<{ eas: Array<{ eaId: string }> }>(`/admin/api/eas`),
  });
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  const otherEas = (easQuery.data?.eas ?? [])
    .map((e) => e.eaId)
    .filter((id) => id !== sourceEaId)
    .sort();

  const allChecked = otherEas.length > 0 && otherEas.every((id) => selected[id]);
  const toggleAll = (on: boolean) => {
    const next: Record<string, boolean> = {};
    for (const id of otherEas) next[id] = on;
    setSelected(next);
  };

  const mutation = useMutation({
    mutationFn: () => {
      const targetEaIds = otherEas.filter((id) => selected[id]);
      return api.post<{ ok: boolean; written: number }>(
        `/admin/api/eas/${encodeURIComponent(sourceEaId)}/indicators/${encodeURIComponent(indicatorType)}/apply-to`,
        { targetEaIds },
      );
    },
    onSuccess: () => onApplied(),
    onError: (err) => setError((err as Error).message),
  });

  const selectedCount = otherEas.filter((id) => selected[id]).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="w-full max-w-md rounded-lg border border-hairline-dark bg-canvas-dark p-5">
        <div className="text-base font-semibold text-on-dark">
          Apply <span className="font-mono">{indicatorType}</span> config to other EAs
        </div>
        <p className="mt-1 text-sm text-muted">
          Copies the SAVED config from <span className="font-mono">{sourceEaId}</span>{" "}
          (allow flag, lot, close mode, TP/SL pips, dynamic-TP thresholds) onto every
          ticked EA. Unsaved local edits are NOT included — save first if you want them.
        </p>

        {easQuery.isLoading ? (
          <div className="mt-4 text-sm text-muted">Loading EAs…</div>
        ) : otherEas.length === 0 ? (
          <div className="mt-4 text-sm text-muted">No other EAs to apply to.</div>
        ) : (
          <>
            <div className="mt-4 flex items-center justify-between">
              <label className="flex items-center gap-2 text-xs text-muted">
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={(e) => toggleAll(e.target.checked)}
                />
                <span>Select all ({otherEas.length})</span>
              </label>
              <span className="text-xs text-muted">{selectedCount} selected</span>
            </div>
            <div className="mt-2 max-h-64 space-y-1 overflow-y-auto rounded-md border border-hairline-dark p-2">
              {otherEas.map((id) => (
                <label
                  key={id}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-surface-elevated"
                >
                  <input
                    type="checkbox"
                    checked={!!selected[id]}
                    onChange={(e) =>
                      setSelected((st) => ({ ...st, [id]: e.target.checked }))
                    }
                  />
                  <span className="font-mono text-on-dark">{id}</span>
                </label>
              ))}
            </div>
          </>
        )}

        {error ? (
          <div className="mt-3 text-sm text-trading-down">{error}</div>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-md border border-hairline-dark px-3 py-2 text-sm text-muted hover:text-on-dark"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={mutation.isPending || selectedCount === 0}
            onClick={() => {
              setError(null);
              mutation.mutate();
            }}
          >
            {mutation.isPending
              ? "Applying…"
              : `Apply to ${selectedCount} EA${selectedCount === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function EquityCard({ equity }: { equity: EaDetailPayload["equity"] }) {
  if (equity.length === 0) {
    return (
      <Card>
        <CardTitle>Equity</CardTitle>
        <div className="text-muted">No heartbeats yet</div>
      </Card>
    );
  }
  const min = Math.min(...equity.map((p) => p.equity));
  const max = Math.max(...equity.map((p) => p.equity));
  const range = max - min || 1;
  const w = 600;
  const h = 80;
  const points = equity
    .map((p, i) => {
      const x = (i / Math.max(equity.length - 1, 1)) * w;
      const y = h - ((p.equity - min) / range) * h;
      return `${x},${y}`;
    })
    .join(" ");
  const last = equity[equity.length - 1];
  const trendUp = equity[equity.length - 1].equity >= equity[0].equity;
  return (
    <Card>
      <CardTitle right={`${equity.length} points · last ${fmtTs(last.ts)}`}>
        Equity curve
      </CardTitle>
      <svg viewBox={`0 0 ${w} ${h}`} className="h-24 w-full">
        <polyline
          fill="none"
          stroke={trendUp ? "#0ecb81" : "#f6465d"}
          strokeWidth={1.5}
          points={points}
        />
      </svg>
      <div className="mt-3 grid grid-cols-3 gap-3">
        <Field label="Latest" value={fmtMoney(last.equity)} />
        <Field label="Min" value={fmtMoney(min)} />
        <Field label="Max" value={fmtMoney(max)} />
      </div>
    </Card>
  );
}
